'use memo';

import { memo } from 'react';
import type { PropsWithChildren } from 'react';
import { ScrollButton } from '@/components/Button/ScrollButton';
import { useScrollableNavigation } from '@/hooks/useScrollableNavigation';
import { cn } from '@/lib/cn';
type Props = PropsWithChildren<{
  /** ルート要素（relative）へ付与するクラス */
  rootClassName?: string;
  /** スクロールviewportへ付与するクラス */
  viewportClassName?: string;
  /** 横並びのコンテンツ要素へ付与するクラス */
  contentClassName?: string;
  /**
   * ScrollButton の背景バリアント
   * - fill: Neutral/Fill（一覧ツールバー向け）
   * - bottomFill: Neutral/Bottom Fill（MegaMenu / Tab 向け。default）
   */
  scrollButtonVariant?: 'fill' | 'bottomFill';
}>;

/**
 * ScrollableRow
 *
 * 横並びの行コンテンツを「1行固定 + シェブロンで横スクロール」にする汎用プリミティブ。
 *
 * - オーバーフロー時のみ左右シェブロンを操作可能にする
 * - クリックで表示領域幅ぶんスクロール
 * - disabled 時もシェブロン DOM は維持（ScrollButton の仕様）
 */
export const ScrollableRow = memo(function ScrollableRow({
  rootClassName,
  viewportClassName,
  contentClassName,
  scrollButtonVariant = 'bottomFill',
  children
}: Props) {
  const {
    containerRef,
    contentRef,
    handleScroll,
    scrollToPrevious,
    scrollToNext,
    isAtStart,
    isAtEnd
  } = useScrollableNavigation();
  return <div className={cn('flex relative min-w-0', rootClassName)}>
      <div ref={containerRef} className={cn(
    // 端付近のフォーカス項目がシェブロン領域に隠れないよう scroll-padding を確保
    'flex overflow-hidden w-full scroll-px-12', viewportClassName)} onScroll={handleScroll}>
        <div ref={contentRef}
      // w-max: コンテンツ幅。min-w-full は ListToolbar 側で足し、非溢出時に右寄せ余地を確保
      // shrink-0: viewport が flex のため、未指定だと内容が潰れてラベルが縦折りになる
      className={cn('flex w-max shrink-0', contentClassName)}>
          {children}
        </div>
      </div>

      <ScrollButton direction="left" onClick={scrollToPrevious} disabled={isAtStart} variant={scrollButtonVariant} />
      <ScrollButton direction="right" onClick={scrollToNext} disabled={isAtEnd} variant={scrollButtonVariant} />
    </div>;
});